import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Edit, Trash2, Users, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import Modal from '../../components/common/Modal';
import { useToast } from '../../components/common/Toast';
import './Departments.css';

function Departments() {
  const toast = useToast();

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDept, setExpandedDept] = useState(null);
  const [members, setMembers] = useState({});

  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteDept, setDeleteDept] = useState(null);

  const loadDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/departments');
      setDepartments(res.departments || []);
    } catch (err) {
      // Don't loop -- just show empty state
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const loadMembers = async (deptId) => {
    try {
      const res = await api.get(`/departments/${deptId}/members`);
      setMembers(prev => ({ ...prev, [deptId]: res.members || [] }));
    } catch (err) {
      toast.error('Failed to load members');
    }
  };

  const toggleExpand = (deptId) => {
    if (expandedDept === deptId) {
      setExpandedDept(null);
    } else {
      setExpandedDept(deptId);
      if (!members[deptId]) {
        loadMembers(deptId);
      }
    }
  };

  const handleCreate = () => {
    setEditDept(null);
    setForm({ name: '', description: '', sort_order: 0 });
    setModalOpen(true);
  };

  const handleEdit = (dept) => {
    setEditDept(dept);
    setForm({
      name: dept.name,
      description: dept.description || '',
      sort_order: dept.sort_order || 0,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Department name is required');
      return;
    }

    setSaving(true);
    try {
      if (editDept) {
        await api.put(`/departments/${editDept.id}`, form);
        toast.success('Department updated');
      } else {
        await api.post('/departments', form);
        toast.success('Department created');
      }
      setModalOpen(false);
      loadDepartments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (dept) => {
    setDeleteDept(dept);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/departments/${deleteDept.id}`);
      toast.success('Department deactivated');
      setDeleteModalOpen(false);
      setDeleteDept(null);
      loadDepartments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate department');
    }
  };

  return (
    <div className="admin-departments">
      <div className="page-header">
        <Building2 size={22} strokeWidth={2} />
        <h1>Departments</h1>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus size={14} />
          Add Department
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading departments...</div>
      ) : departments.length === 0 ? (
        <div className="empty-state">
          <Building2 size={40} strokeWidth={1.5} />
          <p>No departments yet. Create your first department to get started.</p>
        </div>
      ) : (
        <div className="departments-list">
          {departments.map((dept) => (
            <div key={dept.id} className="department-card">
              <div className="department-header" onClick={() => toggleExpand(dept.id)}>
                <div className="department-expand">
                  {expandedDept === dept.id ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </div>
                <div className="department-info">
                  <span className="department-name">{dept.name}</span>
                  {dept.description && (
                    <span className="department-description">{dept.description}</span>
                  )}
                </div>
                <div className="department-meta">
                  <span className="member-count">
                    <Users size={14} />
                    {dept.member_count} {dept.member_count === 1 ? 'member' : 'members'}
                  </span>
                </div>
                <div className="department-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="btn-icon" onClick={() => handleEdit(dept)} title="Edit">
                    <Edit size={14} />
                  </button>
                  <button className="btn-icon btn-icon-danger" onClick={() => handleDeleteClick(dept)} title="Deactivate">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {expandedDept === dept.id && (
                <div className="department-members">
                  {!members[dept.id] ? (
                    <div className="members-loading">Loading members...</div>
                  ) : members[dept.id].length === 0 ? (
                    <div className="members-empty">No members in this department</div>
                  ) : (
                    <table className="members-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Manager</th>
                          <th>Primary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members[dept.id].map((member) => (
                          <tr key={member.id}>
                            <td>{member.name}</td>
                            <td className="member-email">{member.email}</td>
                            <td>
                              <span className={`role-badge role-${member.role}`}>
                                {member.role}
                              </span>
                            </td>
                            <td>{member.is_manager ? 'Yes' : ''}</td>
                            <td>{member.is_primary ? 'Yes' : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editDept ? 'Edit Department' : 'Create Department'}
      >
        <div className="department-form">
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Department name"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Sort Order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
              min={0}
            />
          </div>
          <div className="modal-actions">
            <button className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editDept ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Deactivate Department"
      >
        <p style={{ marginBottom: 'var(--space-4)', color: 'var(--gray-600)' }}>
          Are you sure you want to deactivate <strong>{deleteDept?.name}</strong>?
          This will hide it from active lists but preserve existing data.
        </p>
        <div className="modal-actions">
          <button className="btn-cancel" onClick={() => setDeleteModalOpen(false)}>Cancel</button>
          <button className="btn-danger" onClick={handleDeleteConfirm}>Deactivate</button>
        </div>
      </Modal>
    </div>
  );
}

export default Departments;
